import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Calendar, CreditCard, Eye, EyeOff, Share2, Users, Gift, Copy, CheckCircle, Send, Loader2, Euro, Link as LinkIcon, Tv, Plus, RefreshCw, MessageSquare, AlertTriangle, Lock, Settings, Star, MessageCircle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import ClientOnboarding from '../../components/Client/ClientOnboarding';
import PurchaseFlowModal from '../../components/Client/PurchaseFlowModal';
import ContentChangeRequestModal from '../../components/Client/ContentChangeRequestModal';
import DeviceSetupModal from '../../components/Client/DeviceSetupModal';
import ClientNotificationBell from '../../components/Client/ClientNotificationBell';

/* ─── Skeleton Loader ─── */
const SkeletonPulse = ({ className }) => (
  <div className={`animate-pulse bg-[#2A2A2D] rounded ${className}`} />
);

const DashboardSkeleton = () => (
  <div className="min-h-screen py-6 sm:py-12 px-3 sm:px-4" data-testid="dashboard-skeleton">
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <SkeletonPulse className="h-9 w-60 mb-2" />
        <SkeletonPulse className="h-5 w-40" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border">
            <SkeletonPulse className="h-4 w-24 mb-3" />
            <SkeletonPulse className="h-8 w-20 mb-1" />
            <SkeletonPulse className="h-3 w-16" />
          </div>
        ))}
      </div>
      <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border mb-6 sm:mb-8">
        <SkeletonPulse className="h-7 w-64 mb-4" />
        <SkeletonPulse className="h-12 w-full mb-3" />
        <SkeletonPulse className="h-20 w-full" />
      </div>
      <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border">
        <SkeletonPulse className="h-7 w-48 mb-4" />
        <SkeletonPulse className="h-40 w-full" />
      </div>
    </div>
  </div>
);

const ClientDashboard = () => {
  const { user, updateUserLocally } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [showCredentials, setShowCredentials] = useState({});
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [referralForm, setReferralForm] = useState({ email: '', full_name: '' });
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState(false);
  const [pricingOffers, setPricingOffers] = useState([]);
  const [contentLists, setContentLists] = useState([]);
  const [showContentChange, setShowContentChange] = useState(false);
  const [deviceSetupSub, setDeviceSetupSub] = useState(null);
  const [deviceSetupM3U, setDeviceSetupM3U] = useState('');
  const [trialLoading, setTrialLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchedRef = useRef(false);
  const actionHandled = useRef(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const [dashRes, pricingRes] = await Promise.all([
        api.get('/client/dashboard-summary'),
        api.get('/pricing-matrix').catch(() => ({ data: { offers: [] } }))
      ]);
      setDashData(dashRes.data);
      if (pricingRes.data?.offers?.length) {
        setPricingOffers(pricingRes.data.offers);
      }
      api.get('/content-lists').then(r => setContentLists(r.data?.lists || [])).catch(() => {});
      if (dashRes.data.review) {
        setReviewForm({ rating: dashRes.data.review.rating, comment: dashRes.data.review.comment });
      }
      if (dashRes.data.user && updateUserLocally) {
        updateUserLocally(dashRes.data.user);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [updateUserLocally]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchData();
  }, [fetchData]);

  // Auto-action depuis les liens email (après chargement des données)
  useEffect(() => {
    if (!dashData || actionHandled.current) return;
    const action = searchParams.get('action');
    const promo = searchParams.get('promo');
    if (action === 'trial') {
      actionHandled.current = true;
      handleRequestTrial();
      setSearchParams({}, { replace: true });
    } else if (action === 'buy') {
      actionHandled.current = true;
      if (promo) {
        setPromoCode(promo);
      }
      setShowPaymentModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [dashData, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Payment return handling
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const stripeSessionId = urlParams.get('stripe_session_id');

    if (paymentStatus === 'cancelled') {
      toast.error('Paiement annulé');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'success' && stripeSessionId) {
      const pollStripeStatus = async (attempts = 0) => {
        if (attempts >= 8) {
          toast.info('Vérification en cours. Votre accès sera activé automatiquement.');
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        try {
          const res = await api.get(`/payment/checkout/status/${stripeSessionId}`);
          if (res.data.payment_status === 'paid') {
            toast.success('Paiement confirmé ! Votre abonnement est activé.');
            window.history.replaceState({}, document.title, window.location.pathname);
            fetchData(true);
            return;
          }
        } catch (err) { /* continue polling */ }
        setTimeout(() => pollStripeStatus(attempts + 1), 2500);
      };
      pollStripeStatus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscriptions = dashData?.subscriptions || [];
  const credentials = dashData?.credentials || [];
  const referralInfo = dashData?.referral || null;
  const myReview = dashData?.review || null;

  const currentCredit = referralInfo?.credit_available ?? user?.credit_available ?? 0;
  const isReferred = !!user?.referred_by;
  const trialUsed = dashData?.trial_used || false;
  const hasAnySub = subscriptions.length > 0;
  const hasActiveSubscription = subscriptions.some((s) => s.status === 'active');
  const hasExpiredTrialOnly = subscriptions.length > 0 && subscriptions.every(s => s.is_trial && s.status === 'expired');

  const copyToClipboard = async (text, field) => {
    if (!text) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      toast.error('Impossible de copier automatiquement, copiez manuellement.');
    }
  };

  const handleSendReferralLink = async (e) => {
    e.preventDefault();
    setSendingLink(true);
    setLinkSuccess(null);
    try {
      const response = await api.post('/client/referral-payment-link', {
        ...referralForm,
        send_email: true
      });
      setLinkSuccess(response.data);
      setReferralForm({ email: '', full_name: '' });
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du lien');
    } finally {
      setSendingLink(false);
    }
  };

  const toggleShowCredential = (credId) => {
    setShowCredentials(prev => ({ ...prev, [credId]: !prev[credId] }));
  };

  const handlePayment = async (durationKey, screens, templateId, isAdult, autoRenew) => {
    setPaymentLoading(true);
    setPaymentError(null);
    try {
      const payload = { origin_url: window.location.origin, screens, duration: durationKey };
      if (templateId != null) payload.content_template_id = templateId;
      if (isAdult) payload.is_adult = true;
      if (autoRenew === true) payload.auto_renewal = true;
      if (promoValidation?.valid) {
        payload.promo_code = promoValidation.code;
      }
      const response = await api.post('/payments/create-checkout', payload);
      if (response.data.status === 'completed') {
        toast.success('Abonnement activé avec votre crédit !');
        setShowPaymentModal(false);
        setPromoCode('');
        setPromoValidation(null);
        fetchData(true);
        return;
      }
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('URL de paiement non disponible');
      }
    } catch (error) {
      console.error('Erreur paiement:', error);
      setPaymentError(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      setPaymentLoading(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    setPromoValidation(null);
    try {
      const response = await api.post('/promo-codes/validate', {
        code: promoCode.trim(),
        user_email: user?.email
      });
      setPromoValidation(response.data);
    } catch (error) {
      setPromoValidation({ valid: false, error: error.response?.data?.detail || 'Code invalide' });
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode('');
    setPromoValidation(null);
  };

  const handleRequestTrial = async () => {
    setTrialLoading(true);
    try {
      await api.post('/client/request-trial');
      fetchData(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la demande d\'essai');
    } finally {
      setTrialLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-[#00FF88] bg-[#00FF88]/10 border-[#00FF88]';
      case 'pending_credentials': return 'text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]';
      case 'expired': return 'text-[#FF3B5C] bg-[#FF3B5C]/10 border-[#FF3B5C]';
      case 'suspended': return 'text-[#FFB800] bg-[#FFB800]/10 border-[#FFB800]';
      default: return 'text-[#B8B8BA] bg-[#3A3A3D]/10 border-[#3A3A3D]';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'pending_credentials': return 'En attente des identifiants';
      case 'expired': return 'Expiré';
      case 'suspended': return 'Suspendu';
      default: return status;
    }
  };

  const getCredentialForSubscription = (subscriptionId) => {
    return credentials.find(c => c.subscription_id === subscriptionId);
  };

  const shouldShowRenewButton = (subscription) => {
    if (subscription.status !== 'active' && subscription.status !== 'expired') return false;
    if (!subscription.end_date) return false;
    const daysRemaining = Math.floor((new Date(subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24));
    return daysRemaining <= 30;
  };

  if (loading && !dashData) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="min-h-screen py-6 sm:py-12 px-3 sm:px-4" data-testid="client-dashboard">
      <ClientOnboarding />
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-white mb-1 sm:mb-2">Tableau de bord</h1>
              <p className="text-sm sm:text-base text-[#B8B8BA]">Bienvenue {user?.full_name}</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <ClientNotificationBell />
              <Link to="/settings" className="flex items-center justify-center gap-2 px-3 sm:px-5 py-2 sm:py-3 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg sm:rounded-xl hover:border-[#00FFFF] transition-all text-white text-sm sm:text-base">
                <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-[#00FFFF]" />
                <span className="hidden sm:inline">Paramètres</span>
              </Link>
              <Link to="/assistance" className="flex items-center justify-center gap-2 px-3 sm:px-5 py-2 sm:py-3 bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg sm:rounded-xl hover:border-[#FF6B35] transition-all text-white text-sm sm:text-base" data-testid="support-link">
                <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
                <span className="hidden sm:inline">Support</span>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="credit-available-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Crédit disponible</span>
              <Euro className="h-4 w-4 sm:h-5 sm:w-5 text-[#00FF88]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-[#00FF88]">{referralInfo?.credit_available?.toFixed(2) || '0.00'}&euro;</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Utilisable</div>
          </div>
          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="credit-pending-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Crédit en attente</span>
              <Euro className="h-4 w-4 sm:h-5 sm:w-5 text-[#FFB800]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-[#FFB800]">{referralInfo?.credit_pending?.toFixed(2) || '0.00'}&euro;</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Accès non validé</div>
          </div>
          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="total-earned-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Total gagné</span>
              <Gift className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B35]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-[#FF6B35]">{referralInfo?.total_credit_earned?.toFixed(2) || '0.00'}&euro;</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Cumul historique</div>
          </div>
          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid="subscription-count">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Abonnements</span>
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-[#B8B8BA]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-white">{subscriptions.length}</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Actifs</div>
          </div>
          <div className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border col-span-2 sm:col-span-1" data-testid="filleuls-count">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#B8B8BA] text-xs sm:text-sm">Filleuls</span>
              <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#FF6B9D]" />
            </div>
            <div className="text-xl sm:text-3xl font-bold text-[#FF6B9D]">{referralInfo?.filleuls_count || 0}</div>
            <div className="text-[#6B6B6E] text-xs mt-1">Parrainés</div>
          </div>
        </div>

        {/* Referral Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center space-x-3">
              <Gift className="h-5 w-5 sm:h-6 sm:w-6 text-[#00FF88]" />
              <h2 className="text-xl sm:text-2xl font-bold text-white">Programme de parrainage</h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Link to="/client/credits-history" className="btn-secondary py-2 px-3 sm:px-4 flex items-center justify-center space-x-2 text-xs sm:text-sm">
                <span>Historique</span>
              </Link>
              {referralInfo?.has_active_subscription ? (
                <button onClick={() => setShowReferralModal(true)} className="btn-primary py-2 px-3 sm:px-4 flex items-center justify-center space-x-2 text-sm">
                  <Send className="h-4 w-4" />
                  <span>Parrainer</span>
                </button>
              ) : (
                <button disabled className="bg-[#3A3A3D] text-[#6B6B6E] py-2 px-3 sm:px-4 flex items-center justify-center space-x-2 rounded-lg cursor-not-allowed text-sm" title="Abonnement actif requis">
                  <Lock className="h-4 w-4" />
                  <span>Parrainer</span>
                </button>
              )}
            </div>
          </div>

          {!referralInfo?.has_active_subscription && (
            <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3 sm:p-4 mb-6 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-[#FFB800] shrink-0 mt-0.5" />
              <div>
                <p className="text-[#FFB800] font-semibold text-sm sm:text-base">Abonnement requis pour parrainer</p>
                <p className="text-[#B8B8BA] text-xs sm:text-sm mt-1">Vous devez avoir un abonnement actif pour utiliser le programme de parrainage.</p>
                <button onClick={() => setShowPaymentModal(true)} className="mt-2 sm:mt-3 text-[#FF6B35] hover:text-[#F7931E] text-xs sm:text-sm font-semibold flex items-center gap-1">
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                  Souscrire maintenant
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            <div className="bg-[#222226] p-3 sm:p-4 rounded-lg">
              <h3 className="text-white font-semibold mb-3 text-sm sm:text-base">Mon lien de parrainage</h3>
              {referralInfo?.has_active_subscription ? (
                <>
                  <div className="flex items-center space-x-2">
                    <input type="text" value={referralInfo?.referral_link || ''} readOnly className="flex-1 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-2 sm:px-3 py-2 text-[#FF6B35] text-xs sm:text-sm font-mono overflow-hidden text-ellipsis" />
                    <button onClick={() => copyToClipboard(referralInfo?.referral_link, 'link')} className="bg-[#FF6B35] text-black p-2 rounded hover:bg-[#00E5E5] transition-colors shrink-0">
                      {copiedField === 'link' ? <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" /> : <Copy className="h-4 w-4 sm:h-5 sm:w-5" />}
                    </button>
                  </div>
                  <p className="text-[#6B6B6E] text-xs mt-2">Code : <span className="text-[#FF6B35] font-mono">{referralInfo?.referral_code || user?.referral_code}</span></p>
                  {/* Partage en 1 clic */}
                  <div className="flex flex-wrap gap-2 mt-3" data-testid="referral-share-buttons">
                    {(() => {
                      const link = referralInfo?.referral_link || '';
                      const msg = `Rejoins IPTV4KFR 📺 Profite d'une réduction sur ton abonnement IPTV avec mon lien : ${link}`;
                      return (
                        <>
                          <a href={`https://wa.me/?text=${encodeURIComponent(msg)}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] text-xs sm:text-sm font-semibold hover:bg-[#25D366]/25 transition-colors"
                            data-testid="share-whatsapp">
                            <MessageCircle className="h-4 w-4" /> WhatsApp
                          </a>
                          <a href={`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent("Rejoins IPTV4KFR 📺 Profite d'une réduction avec mon lien !")}`} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#229ED9]/15 border border-[#229ED9]/40 text-[#229ED9] text-xs sm:text-sm font-semibold hover:bg-[#229ED9]/25 transition-colors"
                            data-testid="share-telegram">
                            <Send className="h-4 w-4" /> Telegram
                          </a>
                          <button onClick={() => copyToClipboard(msg, 'share-msg')}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#3A3A3D]/40 border border-[#3A3A3D] text-white text-xs sm:text-sm font-semibold hover:border-[#FF6B35] transition-colors"
                            data-testid="share-copy-message">
                            {copiedField === 'share-msg' ? <CheckCircle className="h-4 w-4 text-[#00FF88]" /> : <Share2 className="h-4 w-4" />} Copier le message
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-14 sm:h-16 bg-[#1A1A1D] border border-dashed border-[#3A3A3D] rounded">
                  <p className="text-[#6B6B6E] text-xs sm:text-sm flex items-center gap-2"><Lock className="h-3 w-3 sm:h-4 sm:w-4" />Disponible avec un abonnement actif</p>
                </div>
              )}
            </div>
            <div className="bg-[#222226] p-3 sm:p-4 rounded-lg">
              <h3 className="text-white font-semibold mb-3 text-sm sm:text-base">Avantages</h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#B8B8BA]">Vous gagnez par filleul</span>
                  <span className="text-[#00FF88] font-bold">{referralInfo?.settings?.bonus_per_referral || 10}&euro;</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#B8B8BA]">Réduction pour votre ami</span>
                  <span className="text-[#FF6B9D] font-bold">{referralInfo?.settings?.discount_for_referred || 5}&euro;</span>
                </div>
              </div>
            </div>
          </div>

          {referralInfo?.filleuls && referralInfo.filleuls.length > 0 && (
            <div className="mt-6">
              <h3 className="text-white font-semibold mb-3 text-sm sm:text-base">Mes filleuls ({referralInfo.filleuls.length})</h3>
              <div className="sm:hidden space-y-3">
                {referralInfo.filleuls.map((filleul) => (
                  <div key={filleul.id} className="bg-[#222226] p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium text-sm">{filleul.full_name}</span>
                      {filleul.has_subscription ? (
                        <span className="text-[#00FF88] text-xs bg-[#00FF88]/10 px-2 py-0.5 rounded">Abonné</span>
                      ) : (
                        <span className="text-[#6B6B6E] text-xs bg-[#3A3A3D]/30 px-2 py-0.5 rounded">En attente</span>
                      )}
                    </div>
                    <p className="text-[#B8B8BA] text-xs">{filleul.email}</p>
                    <p className="text-[#6B6B6E] text-xs mt-1">{filleul.created_at ? new Date(filleul.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                  </div>
                ))}
              </div>
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#6B6B6E] border-b border-[#3A3A3D]">
                      <th className="py-2 text-left">Nom</th>
                      <th className="py-2 text-left">Email</th>
                      <th className="py-2 text-center">Abonné</th>
                      <th className="py-2 text-right">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralInfo.filleuls.map((filleul) => (
                      <tr key={filleul.id} className="border-b border-[#3A3A3D]/50">
                        <td className="py-2 text-white">{filleul.full_name}</td>
                        <td className="py-2 text-[#B8B8BA]">{filleul.email}</td>
                        <td className="py-2 text-center">{filleul.has_subscription ? <span className="text-[#00FF88]">&#10003;</span> : <span className="text-[#6B6B6E]">-</span>}</td>
                        <td className="py-2 text-right text-[#6B6B6E]">{filleul.created_at ? new Date(filleul.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </motion.div>

        {/* Subscriptions & Credentials */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center space-x-3">
              <Tv className="h-5 w-5 sm:h-6 sm:w-6 text-[#FF6B35]" />
              <span>Mes accès IPTV</span>
            </h2>
            <button onClick={() => setShowPaymentModal(true)} className="btn-secondary py-2 px-3 sm:px-4 flex items-center justify-center space-x-2 text-sm">
              <Plus className="h-4 w-4" />
              <span>Nouvel accès</span>
            </button>
          </div>

          <div className="space-y-4" data-testid="subscriptions-list">
            {subscriptions.length === 0 ? (
              <div className="bg-[#1A1A1D] p-6 sm:p-8 rounded-lg metallic-border text-center">
                <Tv className="h-10 w-10 sm:h-12 sm:w-12 text-[#6B6B6E] mx-auto mb-4" />
                <p className="text-[#B8B8BA] mb-4 text-sm sm:text-base">Aucun abonnement actif</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {!trialUsed && (
                    <button onClick={handleRequestTrial} disabled={trialLoading} className="px-6 py-3 bg-[#00FFFF]/10 border border-[#00FFFF] text-[#00FFFF] rounded-lg hover:bg-[#00FFFF]/20 transition-colors font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="request-trial-btn">
                      {trialLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tv className="h-4 w-4" />}
                      Essai gratuit 24h
                    </button>
                  )}
                  <button onClick={() => setShowPaymentModal(true)} className="btn-primary text-sm sm:text-base" data-testid="subscribe-btn">Souscrire maintenant</button>
                </div>
              </div>
            ) : hasExpiredTrialOnly ? (
              <div className="bg-[#1A1A1D] p-6 sm:p-8 rounded-lg metallic-border text-center">
                <Tv className="h-10 w-10 sm:h-12 sm:w-12 text-[#FF6B35] mx-auto mb-4" />
                <p className="text-white font-semibold mb-2 text-base">Votre essai gratuit est terminé</p>
                <p className="text-[#B8B8BA] mb-4 text-sm">Passez à un abonnement complet pour continuer à profiter du service.</p>
                <button onClick={() => setShowPaymentModal(true)} className="btn-primary text-sm sm:text-base px-8" data-testid="buy-after-trial-btn">
                  <CreditCard className="h-4 w-4 inline mr-2" />Acheter l'accès
                </button>
              </div>
            ) : (
              subscriptions.map((sub) => {
                const cred = getCredentialForSubscription(sub.id);
                const totalScreens = sub.screens || 1;
                const configuredCount = (sub.device_setups || []).filter((d) => d.device_type).length;
                const unconfigured = Math.max(0, totalScreens - configuredCount);
                return (
                  <div key={sub.id} className="bg-[#1A1A1D] p-4 sm:p-6 rounded-lg metallic-border" data-testid={`subscription-${sub.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 mb-4">
                      <span className="text-white font-semibold text-base sm:text-lg">Abonnement IPTV4KFR</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border self-start sm:self-auto ${getStatusColor(sub.status)}`} data-testid={`subscription-status-${sub.id}`}>
                        {getStatusLabel(sub.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                      <div className="space-y-3">
                        <h4 className="text-[#FF6B35] font-semibold text-xs sm:text-sm flex items-center space-x-2">
                          <Calendar className="h-4 w-4" /><span>Informations abonnement</span>
                        </h4>
                        <div className="space-y-2 text-sm bg-[#222226] p-3 sm:p-4 rounded-lg">
                          <div className="flex justify-between">
                            <span className="text-[#B8B8BA]">Début :</span>
                            <span className="text-white text-xs sm:text-sm">{formatDate(sub.start_date)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#B8B8BA]">Expiration :</span>
                            <span className="text-white font-semibold text-xs sm:text-sm">{formatDate(sub.end_date)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#B8B8BA]">Renouvellement auto :</span>
                            <span className={`text-xs sm:text-sm ${sub.auto_renewal ? 'text-[#00FF88]' : 'text-[#B8B8BA]'}`}>{sub.auto_renewal ? 'Actif' : 'Inactif'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <h4 className="text-[#00FF88] font-semibold text-xs sm:text-sm flex items-center space-x-2">
                          <CreditCard className="h-4 w-4" /><span>Identifiants IPTV</span>
                        </h4>
                        {cred ? (
                          <div className="space-y-2 text-sm bg-[#222226] p-3 sm:p-4 rounded-lg">
                            <div className="flex justify-between items-center">
                              <span className="text-[#B8B8BA] text-xs sm:text-sm">Utilisateur :</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-white font-mono text-xs sm:text-sm">{showCredentials[cred.id] ? cred.username : '\u2022'.repeat(8)}</span>
                                <button onClick={() => toggleShowCredential(cred.id)} className="text-[#FF6B35] hover:text-[#00E5E5]">
                                  {showCredentials[cred.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                              </div>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#B8B8BA] text-xs sm:text-sm">Mot de passe :</span>
                              <span className="text-white font-mono text-xs sm:text-sm">{showCredentials[cred.id] ? cred.password : '\u2022'.repeat(8)}</span>
                            </div>
                            {cred.server_url && (
                              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                                <span className="text-[#B8B8BA] text-xs sm:text-sm">Serveur :</span>
                                <span className="text-white font-mono text-xs break-all">{cred.server_url}{cred.port ? `:${cred.port}` : ''}</span>
                              </div>
                            )}
                            {cred.m3u_link && (
                              <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-[#3A3A3D]/50">
                                <span className="text-[#B8B8BA] text-xs sm:text-sm">Lien M3U :</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-[#00FFFF] font-mono text-xs break-all flex-1">{cred.m3u_link}</span>
                                  <button onClick={() => copyToClipboard(cred.m3u_link, `m3u-${cred.id}`)} className="text-[#00FFFF] hover:text-white p-1 shrink-0" title="Copier le lien M3U">{copiedField === `m3u-${cred.id}` ? <CheckCircle className="h-3 w-3" /> : <Copy className="h-3 w-3" />}</button>
                                </div>
                              </div>
                            )}
                            {cred.smarters_url && (
                              <div className="flex flex-col gap-1 mt-2 pt-2 border-t border-[#3A3A3D]/50">
                                <span className="text-[#B8B8BA] text-xs sm:text-sm">URL Smarters Samsung / LG :</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-[#FFB800] font-mono text-xs break-all flex-1">{cred.smarters_url}</span>
                                  <button onClick={() => copyToClipboard(cred.smarters_url, `smarters-${cred.id}`)} className="text-[#FFB800] hover:text-white p-1 shrink-0" title="Copier l'URL Smarters">{copiedField === `smarters-${cred.id}` ? <CheckCircle className="h-3 w-3" /> : <Copy className="h-3 w-3" />}</button>
                                </div>
                              </div>
                            )}
                            {cred.access_name && (
                              <div className="flex justify-between mt-2 pt-2 border-t border-[#3A3A3D]/50">
                                <span className="text-[#B8B8BA] text-xs sm:text-sm">Nom de l'accès :</span>
                                <span className="text-[#FF6B35] text-xs sm:text-sm font-medium">{cred.access_name}</span>
                              </div>
                            )}
                            {cred.qr_url && (
                              <div className="mt-3 pt-3 border-t border-[#3A3A3D]/50 flex flex-col items-center text-center" data-testid="client-qr-block">
                                <span className="text-[#FF6B35] text-xs sm:text-sm font-semibold mb-2">📱 Configuration rapide par QR code</span>
                                <img
                                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(cred.qr_url)}`}
                                  alt="QR code de configuration IPTV"
                                  width="160" height="160"
                                  className="bg-white p-2 rounded-lg"
                                  data-testid="client-qr-image"
                                />
                                <span className="text-[#6B6B6E] text-[11px] mt-2">Scannez avec votre application IPTV compatible pour configurer automatiquement.</span>
                                <button
                                  onClick={() => setShowContentChange(true)}
                                  className="mt-2 text-[#FFB800] text-[11px] underline hover:text-[#FFC400] transition-colors"
                                  data-testid="request-content-change-btn"
                                >
                                  Changer ma liste de chaînes / contenu adulte
                                </button>
                              </div>
                            )}
                            <button
                              onClick={() => { setDeviceSetupSub(sub); setDeviceSetupM3U(cred.m3u_link || ''); }}
                              className={`mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg border text-xs sm:text-sm transition-colors ${unconfigured > 0 ? 'border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800] hover:bg-[#FFB800]/20' : 'border-[#3A3A3D] text-white hover:border-[#FF6B35]'}`}
                              data-testid="configure-devices-btn"
                            >
                              <Settings className={`h-4 w-4 ${unconfigured > 0 ? 'text-[#FFB800]' : 'text-[#FF6B35]'}`} />
                              {unconfigured > 0
                                ? `Configurer mes appareils — ${unconfigured} écran${unconfigured > 1 ? 's' : ''} à finaliser`
                                : `Configurer mes appareils${totalScreens > 1 ? ` (${totalScreens} écrans)` : ''}`}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 p-3 sm:p-4 rounded-lg">
                            <p className="text-[#FFB800] text-xs sm:text-sm">Vos identifiants seront envoyés par email dans les prochaines heures.</p>
                          </div>
                        )}
                      </div>
                    </div>
                    {shouldShowRenewButton(sub) && (
                      <div className="mt-4 pt-4 border-t border-[#3A3A3D]">
                        <button onClick={() => setShowPaymentModal(true)} className="w-full btn-primary py-2.5 sm:py-3 flex items-center justify-center space-x-2 hover:bg-[#00E5E5] transition-colors text-sm sm:text-base">
                          <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" /><span>Renouveler</span>
                        </button>
                        {currentCredit > 0 && (
                          <p className="text-[#00FF88] text-xs sm:text-sm text-center mt-2">Crédit de {currentCredit.toFixed(2)}&euro; déduit automatiquement</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </div>

      {/* Referral Modal */}
      {showReferralModal && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <motion.div initial={{ opacity: 0, y: 100 }} animate={{ opacity: 1, y: 0 }} className="bg-[#1A1A1D] rounded-t-2xl sm:rounded-lg metallic-border w-full sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-white flex items-center space-x-2">
                <Gift className="h-5 w-5 text-[#00FF88]" /><span>Parrainer un ami</span>
              </h3>
              <button onClick={() => { setShowReferralModal(false); setLinkSuccess(null); }} className="text-[#6B6B6E] hover:text-white p-2">&#10005;</button>
            </div>
            {linkSuccess ? (
              <div className="text-center py-4">
                <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-[#00FF88] mx-auto mb-4" />
                <h4 className="text-white font-bold text-base sm:text-lg mb-2">Lien envoyé !</h4>
                <p className="text-[#B8B8BA] text-sm mb-4">Un email avec le lien de paiement a été envoyé à votre ami.</p>
                <div className="bg-[#222226] p-3 rounded-lg mb-4">
                  <p className="text-[#6B6B6E] text-xs mb-1">Lien de paiement :</p>
                  <div className="flex items-center space-x-2">
                    <input type="text" value={linkSuccess.payment_url} readOnly className="flex-1 bg-[#1A1A1D] border border-[#3A3A3D] rounded px-2 py-1 text-[#FF6B35] text-xs font-mono" />
                    <button onClick={() => copyToClipboard(linkSuccess.payment_url, 'newLink')} className="text-[#FF6B35] hover:text-white p-1">
                      {copiedField === 'newLink' ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <button onClick={() => { setShowReferralModal(false); setLinkSuccess(null); }} className="btn-primary w-full py-3">Fermer</button>
              </div>
            ) : (
              <form onSubmit={handleSendReferralLink} className="space-y-4">
                <p className="text-[#B8B8BA] text-xs sm:text-sm mb-4">
                  Envoyez un lien personnalisé à un ami. Réduction de{' '}
                  <span className="text-[#FF6B9D] font-bold">{referralInfo?.settings?.discount_for_referred || 5}&euro;</span> pour lui,
                  et{' '}<span className="text-[#00FF88] font-bold">{referralInfo?.settings?.bonus_per_referral || 10}&euro;</span> de crédit pour vous !
                </p>
                <div>
                  <label className="block text-[#E8E8E8] text-sm mb-1">Nom complet</label>
                  <input type="text" value={referralForm.full_name} onChange={(e) => setReferralForm({...referralForm, full_name: e.target.value})} className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none text-base" placeholder="Prénom Nom" required />
                </div>
                <div>
                  <label className="block text-[#E8E8E8] text-sm mb-1">Email</label>
                  <input type="email" value={referralForm.email} onChange={(e) => setReferralForm({...referralForm, email: e.target.value})} className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FF6B35] focus:outline-none text-base" placeholder="ami@email.com" required />
                </div>
                <div className="bg-[#1A1A1D] border border-[#3A3A3D] rounded-lg p-3 sm:p-4">
                  <div className="flex justify-between items-center">
                    <div><span className="text-[#B8B8BA] text-xs sm:text-sm">Offre unique</span><div className="text-white font-bold text-sm sm:text-base">IPTV 12 mois</div></div>
                    <div className="text-[#FF6B35] font-bold text-lg sm:text-xl">59&euro;/an</div>
                  </div>
                </div>
                <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#B8B8BA]">Prix pour votre ami :</span>
                    <span className="text-[#00FF88] font-bold">{Math.max(0, 59 - (referralInfo?.settings?.discount_for_referred || 5))}&euro;</span>
                  </div>
                </div>
                <button type="submit" disabled={sendingLink} className="btn-primary w-full py-3 flex items-center justify-center space-x-2 disabled:opacity-50">
                  {sendingLink ? (<><Loader2 className="h-5 w-5 animate-spin" /><span>Envoi...</span></>) : (<><Send className="h-5 w-5" /><span>Envoyer le lien</span></>)}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      )}

      {/* Payment Modal — parcours 3 étapes (Durée → Écrans → Résumé) */}
      {showPaymentModal && (
        <PurchaseFlowModal
          offers={pricingOffers}
          contentLists={contentLists}
          currentCredit={currentCredit}
          isReferred={isReferred}
          referralDiscount={(isReferred && !user?.has_used_referral_discount && !referralInfo?.has_active_subscription) ? (referralInfo?.settings?.discount_for_referred || 0) : 0}
          promoCode={promoCode}
          setPromoCode={setPromoCode}
          promoValidation={promoValidation}
          promoValidating={promoValidating}
          validatePromoCode={validatePromoCode}
          clearPromoCode={clearPromoCode}
          paymentLoading={paymentLoading}
          paymentError={paymentError}
          onClose={() => { setShowPaymentModal(false); setPaymentError(null); clearPromoCode(); }}
          onPay={handlePayment}
        />
      )}

      {showContentChange && (
        <ContentChangeRequestModal
          contentLists={contentLists}
          onClose={() => setShowContentChange(false)}
        />
      )}

      {deviceSetupSub && (
        <DeviceSetupModal
          subscription={deviceSetupSub}
          m3uLink={deviceSetupM3U}
          onClose={() => setDeviceSetupSub(null)}
          onSaved={() => fetchData(true)}
        />
      )}

      {/* Review Section — réservée aux abonnés actifs */}
      {hasActiveSubscription && (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-gradient-to-br from-[#1A1A1D] to-[#121214] p-6 rounded-lg metallic-border" data-testid="review-section">
        <div className="flex items-center space-x-3 mb-4">
          <Star className="h-6 w-6 text-[#FFB800]" />
          <h3 className="text-xl font-bold text-white">{myReview ? 'Votre avis' : 'Donnez votre avis'}</h3>
        </div>
        {reviewSuccess && (
          <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-3 mb-4 flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-[#00FF88]" />
            <span className="text-[#00FF88] text-sm">{myReview ? 'Avis mis à jour !' : 'Merci pour votre avis !'}</span>
          </div>
        )}
        {myReview && myReview.status === 'pending' && (
          <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3 mb-4">
            <p className="text-[#FFB800] text-sm">Votre avis est en attente de validation par notre équipe.</p>
          </div>
        )}
        <div className="space-y-4">
          <div>
            <label className="block text-[#B8B8BA] text-sm mb-2">Note</label>
            <div className="flex space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button key={star} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: star })} className="p-1 transition-transform hover:scale-110" data-testid={`star-rating-${star}`}>
                  <Star className={`h-7 w-7 ${star <= reviewForm.rating ? 'text-[#FFB800] fill-[#FFB800]' : 'text-[#3A3A3D]'}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[#B8B8BA] text-sm mb-2">Votre commentaire</label>
            <textarea value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} rows="3" className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#FFB800] focus:outline-none text-sm" placeholder="Partagez votre expérience avec IPTV4KFR..." data-testid="review-comment-input" />
          </div>
          <button onClick={async () => {
            setReviewLoading(true);
            try {
              if (myReview) { await api.put('/client/reviews', reviewForm); } else { await api.post('/client/reviews', reviewForm); }
              setReviewSuccess(true);
              fetchData(true);
              setTimeout(() => setReviewSuccess(false), 3000);
            } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } finally { setReviewLoading(false); }
          }} disabled={reviewLoading || !reviewForm.comment.trim()} className="btn-primary py-2 px-6 text-sm disabled:opacity-50 flex items-center space-x-2" data-testid="submit-review-btn">
            <Star className="h-4 w-4" /><span>{reviewLoading ? 'Envoi...' : myReview ? 'Modifier mon avis' : 'Envoyer mon avis'}</span>
          </button>
        </div>
      </motion.div>
      )}
    </div>
  );
};

export default ClientDashboard;
